<template>
  <Modal
    :model-value="true"
    title="Manage Users - Dialpad"
    size="large"
    show-close-button
    @update:model-value="emit('close')"
  >
    <p class="mb-4 text-sm text-gray-700">
      Choose the Dialpad user each Sera user signs in to the dialer as. Users
      without a Dialpad user can't use the softphone.
    </p>

    <div v-if="loading" class="flex justify-center py-6">
      <Spinner />
    </div>

    <template v-else>
      <div
        v-if="usersError"
        class="mb-4 flex items-center gap-3"
        data-cy="dialpad-users-error"
      >
        <Alert type="error" :text="usersError" class="flex-1" />
        <Button
          label="Retry"
          type="Secondary"
          size="small"
          data-cy="dialpad-users-retry"
          @click="loadDialpadUsers"
        />
      </div>

      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-gray-500">
            <th class="py-2 pr-4 font-medium">Sera user</th>
            <th class="py-2 font-medium">Dialpad user</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="admin in admins"
            :key="admin.id"
            class="border-t border-gray-200"
            :data-cy="`dialpad-mapping-row-${admin.id}`"
          >
            <td class="py-2 pr-4">
              <div class="font-medium text-gray-900">{{ admin.name }}</div>
              <div class="text-gray-500">{{ admin.email }}</div>
            </td>
            <td class="py-2">
              <SelectInput
                :model-value="admin.dialpad_user_id"
                :options="optionsByAdmin.get(admin.id) ?? []"
                :disabled="!!usersError || savingId === admin.id"
                placeholder="No Dialpad user"
                filter
                :data-cy="`dialpad-mapping-select-${admin.id}`"
                @update:model-value="(value) => assign(admin, value)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <!-- Each choice saves as it's made, so there is nothing left to submit. -->
    <template #footer>
      <div class="flex justify-end px-4 py-3 md:px-5">
        <Button
          label="Done"
          data-cy="dialpad-mappings-done"
          @click="emit('close')"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import Alert from "@/components/Seranova/Alert/Alert.vue";
import Button from "@/components/Seranova/Buttons/Button.vue";
import Modal from "@/components/Seranova/Modal/Modal.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import type { SelectInputOption } from "@/components/Seranova/SelectInput/SelectInputOptions.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import { setMappedDialpadUser } from "@/composables/useDialpadContext";

interface MappedAdmin {
  id: number;
  name: string;
  email: string;
  dialpad_user_id: string | null;
}

interface DialpadUser {
  id: string;
  name: string;
  email: string | null;
}

const UNKNOWN_USER = "Unknown Dialpad user";
const LOAD_USERS_FAILED = "Could not load Dialpad users. Try again.";

const emit = defineEmits<{ (e: "close"): void }>();

const store = useStore();

const admins = ref<MappedAdmin[]>([]);
const dialpadUsers = ref<DialpadUser[]>([]);
const loading = ref(true);
const usersError = ref("");
const savingId = ref<number | null>(null);

const allOptions = computed<SelectInputOption[]>(() =>
  dialpadUsers.value.map((user) => ({
    value: user.id,
    label: user.email ? `${user.name} (${user.email})` : user.name
  }))
);

// Each row leaves out the Dialpad users other admins hold. A mapped id Dialpad no
// longer lists still shows, so it can be seen and reassigned.
const optionsByAdmin = computed(() => {
  const holders = new Map<string, number>();
  admins.value.forEach((admin) => {
    if (admin.dialpad_user_id) holders.set(admin.dialpad_user_id, admin.id);
  });
  const known = new Set(dialpadUsers.value.map((user) => user.id));

  return new Map<number, SelectInputOption[]>(
    admins.value.map((admin): [number, SelectInputOption[]] => {
      const options = allOptions.value.filter((option) => {
        const holder = holders.get(String(option.value));
        return holder === undefined || holder === admin.id;
      });
      const current = admin.dialpad_user_id;
      if (current && !known.has(current)) {
        options.unshift({ value: current, label: UNKNOWN_USER });
      }
      return [admin.id, options];
    })
  );
});

function serverMessage(error: unknown): string | undefined {
  return (error as { response?: { data?: { message?: string } } })?.response
    ?.data?.message;
}

async function loadDialpadUsers() {
  usersError.value = "";
  try {
    dialpadUsers.value = await store.dispatch("fetchDialpadUsers");
  } catch (error) {
    usersError.value = serverMessage(error) || LOAD_USERS_FAILED;
  }
}

async function assign(admin: MappedAdmin, value: string | number | null) {
  const dialpadUserId = value === null ? null : String(value);
  if (dialpadUserId === admin.dialpad_user_id) return;

  savingId.value = admin.id;
  try {
    const saved = await store.dispatch("updateDialpadUserMapping", {
      adminId: admin.id,
      dialpadUserId
    });
    admin.dialpad_user_id = saved.dialpad_user_id;
    // The page's softphone was set up for the old mapping; linking yourself
    // takes effect now rather than on the next full page load.
    if (admin.id === store.state.currentAdmin?.id) {
      setMappedDialpadUser(saved.dialpad_user_id);
    }
    window.notify.success(`Dialpad user saved for ${admin.name}`);
  } catch (error) {
    window.notify.error(
      serverMessage(error) || "Unable to save the Dialpad user"
    );
  } finally {
    savingId.value = null;
  }
}

onMounted(async () => {
  try {
    const [mappings] = await Promise.all([
      store.dispatch("fetchDialpadUserMappings"),
      loadDialpadUsers()
    ]);
    admins.value = mappings;
  } catch {
    window.notify.error("Unable to load Sera users");
  } finally {
    loading.value = false;
  }
});
</script>
